<template>
  <div ref="sliderEl" class="content-slider">
    <Transition name="content-slider-fade" mode="out-in">
      <div :key="activeIndex" class="content-slider__slide">
        <StoryblokComponent
          v-for="blok in slides[activeIndex]?.content"
          :key="blok._uid"
          :blok="blok"
        />
      </div>
    </Transition>

    <div v-if="slides.length > 1" class="content-slider__bars">
      <button
        v-for="(slide, i) in slides"
        :key="slide._uid"
        class="content-slider__bar-btn"
        :aria-label="`Slide ${i + 1}`"
        @click="onBarClick(i)"
      >
        <span class="content-slider__bar">
          <span
            v-if="activeIndex === i && isAutoPlaying"
            :key="`fill-${fillKey}`"
            class="content-slider__bar-fill"
            :style="{ '--duration': `${duration}s` }"
          />
          <span
            v-else-if="activeIndex === i"
            class="content-slider__bar-fill content-slider__bar-fill--instant"
          />
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SbBlokData } from '@storyblok/js'

export interface ContentSlide {
  _uid: string
  content: SbBlokData[]
}

interface Props {
  slides: ContentSlide[]
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  duration: 10,
})

const sliderEl = ref<HTMLElement | null>(null)
const activeIndex = ref(0)
const isAutoPlaying = ref(false)
const userStopped = ref(false)
const fillKey = ref(0)

let timer: ReturnType<typeof setTimeout> | null = null

function advance() {
  if (!isAutoPlaying.value) return
  activeIndex.value = (activeIndex.value + 1) % props.slides.length
  fillKey.value++
  timer = setTimeout(advance, props.duration * 1000)
}

function startAutoPlay() {
  if (isAutoPlaying.value || userStopped.value || props.slides.length < 2) return
  isAutoPlaying.value = true
  fillKey.value++
  timer = setTimeout(advance, props.duration * 1000)
}

function stopAutoPlay() {
  isAutoPlaying.value = false
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

function onBarClick(index: number) {
  userStopped.value = true
  stopAutoPlay()
  activeIndex.value = index
}

onMounted(() => {
  const observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting) {
        startAutoPlay()
      } else {
        stopAutoPlay()
      }
    },
    { threshold: 0.2 },
  )
  if (sliderEl.value) observer.observe(sliderEl.value)
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<style lang="scss" scoped>
.content-slider {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__slide {
    flex: 1;
  }

  &__bars {
    display: flex;
    gap: 4px;
    margin-top: 24px;
  }

  &__bar-btn {
    flex: 1;
    background: none;
    border: none;
    padding: 8px 0;
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  &__bar {
    display: block;
    position: relative;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: var(--paper-200);
    overflow: hidden;
  }

  &__bar-fill {
    position: absolute;
    inset: 0;
    background: var(--cobalt-500);
    transform-origin: left center;
    animation: content-slider-fill var(--duration, 10s) linear forwards;

    &--instant {
      animation: none;
      transform: scaleX(1);
    }
  }
}

@keyframes content-slider-fill {
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
}

// ── Slide transition ──────────────────────────────────────────────────────────

.content-slider-fade-enter-active,
.content-slider-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.content-slider-fade-enter-from {
  opacity: 0;
  transform: translateX(12px);
}

.content-slider-fade-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}
</style>
